import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { MapPin, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import PageMeta from '../components/PageMeta';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. No token provided.');
      return;
    }
    const verify = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API}/api/auth/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
        const data = await res.json();
        if (res.ok) {
          setStatus('success');
          setMessage(data.message || 'Email verified successfully!');
        } else {
          setStatus('error');
          setMessage(data.detail || 'Verification failed.');
        }
      } catch {
        setStatus('error');
        setMessage('Something went wrong. Please try again.');
      }
    };
    verify();
  }, [token]);

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-6">
      <PageMeta title="Verify Email - GMB Autopilot" />
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="w-8 h-8 bg-[#1A73E8] rounded-lg flex items-center justify-center">
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>GMB Autopilot</span>
        </Link>

        {status === 'verifying' && (
          <div data-testid="verifying-state">
            <Loader2 className="w-12 h-12 text-[#1A73E8] animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#202124]">Verifying your email...</h2>
            <p className="text-[#5F6368] mt-2">Please wait a moment.</p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="success-state">
            <CheckCircle className="w-16 h-16 text-[#1E8E3E] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#202124]">Email Verified!</h2>
            <p className="text-[#5F6368] mt-2">{message}</p>
            <Link to="/login" className="btn-google inline-block mt-6" data-testid="go-login-btn">Sign In Now</Link>
          </div>
        )}

        {status === 'error' && (
          <div data-testid="error-state">
            <XCircle className="w-16 h-16 text-[#D93025] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#202124]">Verification Failed</h2>
            <p className="text-[#5F6368] mt-2">{message}</p>
            <div className="mt-6 space-y-3">
              <Link to="/login" className="btn-google inline-block" data-testid="go-login-btn">Go to Sign In</Link>
              <p className="text-sm text-[#9AA0A6]">You can request a new verification email from the login page.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
